/**
 * FileViewerDialog
 *
 * Modal wrapper around `CustomerAttachmentViewer`. Renders the file's
 * name + icon in the header, the live viewer in the body, and Close /
 * Download actions in the footer.
 *
 * Extracted from `file-manager-panel.tsx` verbatim to keep the panel
 * orchestrator small. Behavior is unchanged.
 */

import {
  Box,
  Button,
  Dialog,
  Typography,
} from "@mui/material"
import { Download, User } from "lucide-react"
import { customerAttachmentsApi } from "@crm-src/lib/customer-attachments-api"
import { CustomerAttachmentViewer } from "./customer-attachment-viewer"
import { FileTypeIcon } from "../file-manager/file-type-icon"
import type { FileItem } from "../file-manager-panel"

interface FileViewerDialogProps {
  isOpen: boolean
  onClose: () => void
  file: FileItem | null
  customerId: string
  onError: (error: string) => void
  onDownload: (file: FileItem) => void
}

export function FileViewerDialog({
  isOpen,
  onClose,
  file,
  customerId,
  onError,
  onDownload,
}: FileViewerDialogProps) {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          overflow: "hidden",
          width: "95vw",
          maxWidth: "95vw",
          height: "90vh",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {file && (
        <>
          <Box sx={{ p: 1.5, pb: 0.5 }}>
            <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FileTypeIcon file={file} />
              {file.name}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {file.content_type} • {customerAttachmentsApi.formatFileSize(file.file_size)}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
            <CustomerAttachmentViewer
              file={file}
              customerId={customerId}
              onError={onError}
              onDownload={() => onDownload(file)}
            />
          </Box>

          <Box sx={{ p: 3, pt: 1, borderTop: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                <User size={16} />
                <Typography variant="body2">Uploaded by: {file.uploaded_by.name}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="outlined" color="inherit" onClick={onClose}>
                  Close
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Download size={16} />}
                  onClick={() => {
                    onDownload(file)
                    onClose()
                  }}
                >
                  Download
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Dialog>
  )
}
